import { inject, injectable, interfaces } from 'inversify';
import { action, makeObservable, runInAction } from 'mobx';
import { v4 } from 'uuid';
import { EditorMode } from '../../../modules/editor/models/EditorModes';
import {
  EditAndAcceptAnnotationRequest,
  EditAndRejectAnnotationRequest,
  IAcceptAnnotationRequest,
  IRejectAnnotationRequest,
} from '../../../modules/editor/models/Requests';
import { IFreeAccessAnnotationApiService } from '../../../modules/editor/services/FreeAccessAnnotationApi.service';
import { ICurrentWorkspaceStore } from '../../../modules/workspaces/currentWorkspace/CurrentWorkspace.store';
import { buildBounds, getLatLngsForGeojson } from '../../helpers/geometry/polygon.helpers';
import { ACDLError, InputStatus } from '../../models/error.model';
import { EventListeningDisposer, IEventBus } from '../../services/eventBus.service';
import { IProjectHubService } from '../../services/projectHub.service';
import { ITimerService } from '../../services/timer.service';
import { IAnnotationService } from '../annotation/annotation.service';
import { IAnnotationApiService } from '../annotation/annotationApi.service';
import { AnnotationStatus, IAnnotationDto, IImage, IQuestionModelDto, ISegmentation, ISegmentationDto } from '../annotation/annotations.interface';
import { IAnnotationsStore } from '../annotation/annotations.store';
import { IAnnotationUiStore } from '../annotation/annotationUi.store';
import { BaseAnnotationControl, IBaseAnnotationControl } from '../annotation/baseAnnotationControl';
import { IFreeDrawSegmentationService } from '../annotation/freeDrawSegmentation.service';
import { cleanClone, IQuestionModel } from '../annotation/question.model';
import { IAnnotationTypeBl } from '../annotation/submodules/annotationTypes/annotationType.bl';
import { ClarificationAddedEventType } from '../annotation/submodules/clarifications/events/ClarificationAddedEvent';
import { IReviewRejectionReasonBl } from '../annotation/submodules/reviewRejectReasones/reviewRejectionReason.bl';
import { IUndoRedoHistory } from '../annotation/undoRedoHistory.service';
import { NotificationLevel, ToastNotification } from '../notifications/models/notification.model';
import { INotificationsService } from '../notifications/services/notifications.service';
import { ImageSetType } from '../projectDetails/sub/images/projectDetailsImages.model';
import { AnnotateStage, FreeAccessMode, IFreeAccessStore } from './freeAccess.store';
import { IFreeAccessApiService } from './freeAccessApi.service';
import { IFreeAccessImagesQueueService } from './freeAccessImagesQueue.service';

export interface IFreeAccessService extends IBaseAnnotationControl {
  initializeAsync(workspaceId: string, projectId: string, imageId: string, filterId: string, mode: FreeAccessMode): Promise<void>;
  rejectFixAnnotationAsync(): Promise<void>;
  unlockImagesAsync(): Promise<void>;
  updateLockImageInfoAsync(): Promise<void>;
  cancelCorrectAnnotationAsync(): Promise<void>;
  cancelReviewAnnotationAsync(): Promise<void>;
  cancelCreateAnnotationAsync(): Promise<void>;
  getProjectAnnotationInfoAsync(projectId: string): Promise<void>;
  changeModeToEdit(): Promise<void>;

  acceptAnnotationAsync(): Promise<void>;
  rejectAnnotationAsync(reason?: string): Promise<void>;
  editAndAcceptOrRejectAnnotationAsync(accept: boolean, rejectionReason?: string): Promise<void>;
  saveAnnotationDraftAsync(): Promise<void>;

  correctAnnotationAsync(): Promise<void>;
  correctAndSubmitAnnotationForReviewAsync(): Promise<void>;
  discardAnnotationAsync(editorMode: EditorMode): Promise<void>;
  editAnnotationAsync(): Promise<void>;
  submitAnnotationsForReviewAsync(): Promise<void>;

  changeModeAsync(mode: FreeAccessMode): Promise<void>;
  updateImageSet(imageSet: ImageSetType): Promise<void>;
}

export namespace IFreeAccessService {
  export const $: interfaces.ServiceIdentifier<IFreeAccessService> = Symbol('FREE_ACCESS_SERVICE');
}
@injectable()
export class FreeAccessService extends BaseAnnotationControl implements IFreeAccessService {
  private eventListenersDisposer: EventListeningDisposer[] = [];
  private projectId: string = '';
  private imageId: string = '';
  private filterId: string = '';

  constructor(
    @inject(IAnnotationApiService.$) private readonly annotationApiService: IAnnotationApiService,
    @inject(IAnnotationService.$) private readonly annotationService: IAnnotationService,
    @inject(IAnnotationTypeBl.$) private readonly annotationTypeBl: IAnnotationTypeBl,
    @inject(IProjectHubService.$) private readonly projectHubService: IProjectHubService,
    @inject(ICurrentWorkspaceStore.$) private readonly currentWorkspaceStore: ICurrentWorkspaceStore,
    @inject(IFreeAccessStore.$) private readonly freeAccessStore: IFreeAccessStore,
    @inject(IFreeAccessImagesQueueService.$) private readonly freeAccessImagesQueueService: IFreeAccessImagesQueueService,
    @inject(IFreeAccessApiService.$) private readonly freeAccessApiService: IFreeAccessApiService,
    @inject(ITimerService.$) private readonly timer: ITimerService,
    @inject(IFreeDrawSegmentationService.$) private readonly freeDrawSegmentationService: IFreeDrawSegmentationService,
    @inject(IUndoRedoHistory.$) private readonly undoRedoService: IUndoRedoHistory,
    @inject(INotificationsService.$) notificationsService: INotificationsService,
    @inject(IAnnotationUiStore.$) uiStore: IAnnotationUiStore,
    @inject(IAnnotationsStore.$) annotationsStore: IAnnotationsStore,
    @inject(IReviewRejectionReasonBl.$) private readonly reviewRejectionReasonBl: IReviewRejectionReasonBl,
    @inject(IEventBus.$) private readonly eventBus: IEventBus,
    @inject(IFreeAccessAnnotationApiService.$) private readonly freeAccessAnnotationApiService: IFreeAccessAnnotationApiService
  ) {
    super(uiStore, notificationsService, annotationsStore);
    this.imagesQueueService = freeAccessImagesQueueService;

    makeObservable<FreeAccessService, 'clarificationAddedListenerAsync'>(this, {
      dispose: action,
      initializeAsync: action,
      changeModeAsync: action,
      updateImageSet: action,
      loadAnnotations: action,
      changeModeToEdit: action,
      requestQuestionsAsync: action,
      refreshQuestionsIfNeededAsync: action,
      rejectAnnotationAsync: action.bound,
      cancelCorrectAnnotationAsync: action.bound,
      cancelReviewAnnotationAsync: action.bound,
      cancelCreateAnnotationAsync: action.bound,
      discardAnnotationAsync: action.bound,
      acceptAnnotationAsync: action.bound,
      showImage: action,
      editAndAcceptOrRejectAnnotationAsync: action,
      rejectFixAnnotationAsync: action,
      clearPreviousImageLockAsync: action,
      unlockImagesAsync: action,
      saveAnnotationDraftAsync: action,
      submitAnnotationsForReviewAsync: action,
      correctAnnotationAsync: action.bound,
      editAnnotationAsync: action.bound,
      correctAndSubmitAnnotationForReviewAsync: action.bound,
      updateLockImageInfoAsync: action.bound,
      clarificationAddedListenerAsync: action.bound,
      getProjectAnnotationInfoAsync: action,
    });
  }

  private get mode(): FreeAccessMode {
    return this.freeAccessStore.freeAccessMode;
  }

  handleImageDisplayed = (): void => {};

  setupAsync = async (): Promise<void> => {
    return Promise.resolve();
  };

  private clarificationAddedListenerAsync = async (): Promise<void> => {
    await this.changeModeAsync(FreeAccessMode.VIEW);
  };

  private addEventListeners(): void {
    this.clearEventListeners();
    this.eventListenersDisposer.push(this.eventBus.addListener(this.clarificationAddedListenerAsync, ClarificationAddedEventType));
  }

  private clearEventListeners(): void {
    this.eventListenersDisposer.forEach((d) => {
      d();
    });
    this.eventListenersDisposer = [];
  }

  dispose(): void {
    this.freeAccessStore.questions.replace([]);
    this.annotationTypeBl.freeAccessFinished();
    this.clearEventListeners();
    super.dispose();
  }

  async initializeAsync(workspaceId: string, projectId: string, imageId: string, filterId: string, mode: FreeAccessMode): Promise<void> {
    if (imageId !== this.imageId) {
      await this.clearPreviousImageLockAsync();
    }

    if (this.projectId !== projectId) {
      this.freeAccessStore.questions.replace([]);
    }

    this.projectId = projectId;
    this.imageId = imageId;
    this.filterId = filterId;
    this.freeAccessStore.setView(mode);
    this.freeDrawSegmentationService.clear();
    this.annotationService.deselectSegmentation();
    this.annotationsStore.setSegmentations([]);
    this.annotationsStore.projectId = projectId;

    const shouldResetSelectedAnnotationType = this.mode === FreeAccessMode.VIEW || this.mode === FreeAccessMode.REVIEW;
    await this.annotationTypeBl.freeAccessStarted(projectId, shouldResetSelectedAnnotationType);
    if (this.annotationsStore.image && this.imageId !== this.annotationsStore.image.id) {
      runInAction(() => {
        this.imagesQueueService.clear();
        this.uiStore.changeIsImageLoading(true);
        this.annotationsStore.setImageName('', '', 'None');
        this.annotationsStore.image = undefined;
        this.cleanupAnnotations();
      });
    }
    await this.refreshQuestionsIfNeededAsync();

    await Promise.all([
      this.freeAccessImagesQueueService.setupAsync(workspaceId, projectId, imageId, filterId, mode),
      this.projectHubService.initializeAsync(),
      this.requestRejectionReasonsAsync(),
    ]);

    this.showImage();
    this.timer.startTimer();

    this.addEventListeners();
  }

  async changeModeAsync(mode: FreeAccessMode, annotateStage?: AnnotateStage): Promise<void> {
    this.freeAccessStore.setView(mode, annotateStage);

    const shouldResetSelectedAnnotationType = mode === FreeAccessMode.VIEW || mode === FreeAccessMode.REVIEW;
    await this.annotationTypeBl.freeAccessStarted(this.annotationsStore.projectId, shouldResetSelectedAnnotationType);

    await this.refreshQuestionsIfNeededAsync();

    await this.freeAccessImagesQueueService.setupAsync(
      this.currentWorkspaceStore.currentWorkspace.id,
      this.annotationsStore.projectId,
      this.imageId,
      this.filterId,
      mode
    );

    this.showImage();
    this.timer.startTimer();
  }

  async updateImageSet(imageSet: ImageSetType): Promise<void> {
    this.freeAccessStore.imageSet = imageSet;
    await this.freeAccessAnnotationApiService.updateProjectImageSetAsync({
      filterId: '',
      projectId: this.annotationsStore.projectId,
      projectImageSet: imageSet,
      selectionMode: 'Deselect',
      toggledImages: [this.imageId],
    });
  }

  cleanupAnnotations(): void {
    this.freeDrawSegmentationService.clear();
    this.annotationsStore.id = undefined;
    this.annotationsStore.setSegmentations([]);
    this.annotationsStore.questions.forEach((q) => {
      q.answers.forEach((a) => {
        a.selected = false;
      });
      q.answer = undefined;
      q.status = InputStatus.empty();
    });
  }

  loadAnnotations(image: IImage): void {
    this.annotationsStore.setHiddenSegmentations([]);
    const annotationInfoResult = image.annotations;

    if (!annotationInfoResult) {
      this.cleanupAnnotations();
      return;
    }

    const annotations = annotationInfoResult.annotations;
    if (annotations === undefined) {
      throw new ACDLError('No annotations found', undefined);
    }
    this.annotationsStore.id = annotationInfoResult.id;
    const segmentations = annotations.segmentations.map((s: ISegmentationDto) => {
      const attributes = this.mapQuestions(s.feature.properties?.annotationTypeId, s.questions);
      const feature = {
        ...s.feature,
        id: v4(),
        color: this.annotationService.getAnnotationTypeColor(s.feature.properties?.annotationTypeId),
        featureType: s.feature.properties?.featureType,
      };

      const latlngs = getLatLngsForGeojson(feature);

      return { feature, latlngs, id: s.id, questions: attributes, bbox: buildBounds(latlngs), priority: s.priority } as ISegmentation;
    });

    this.annotationsStore.setSegmentations(segmentations.filter((s) => s.feature.color !== ''));
    this.annotationsStore.questions.replace(this.mapQuestions(undefined, annotationInfoResult.annotations.questions));
    this.annotationService.deselectSegmentation();
  }

  async requestRejectionReasonsAsync(): Promise<void> {
    await this.reviewRejectionReasonBl.getReasons(this.projectId);
  }

  async changeModeToEdit(): Promise<void> {
    await this.changeModeAsync(FreeAccessMode.REVIEWCORRECT);
  }

  mapQuestions(annotationTypeId: string | undefined, questionsDtos: IQuestionModelDto[]): IQuestionModel[] {
    const currentQuestions = annotationTypeId
      ? this.freeAccessStore.questions.filter((q) => q.scopes.includes(annotationTypeId))
      : this.freeAccessStore.questions;

    const models: IQuestionModel[] = [];
    for (const currentQuestion of currentQuestions) {
      const existingAnswer = questionsDtos.find((x) => x.id === currentQuestion.id);
      models.push(cleanClone(currentQuestion, existingAnswer));
    }

    return models;
  }

  async requestQuestionsAsync(): Promise<IQuestionModel[]> {
    const result = await this.annotationApiService.requestQuestionsAsync(this.projectId);
    if (result instanceof Error) {
      throw result;
    }
    return result;
  }

  assignQuestions(): void {
    this.annotationsStore.questions.replace(this.mapQuestions(undefined, this.freeAccessStore.questions));
    this.annotationService.deselectSegmentation();
  }

  async refreshQuestionsIfNeededAsync(): Promise<void> {
    const isEmpty = this.freeAccessStore.questions.length === 0;

    if (isEmpty) {
      const questions = await this.requestQuestionsAsync();

      this.freeAccessStore.questions.replace(questions);
      this.assignQuestions();
      return;
    }

    if (
      this.projectHubService.didQuestionsChangedFor(this.annotationsStore.projectId) ||
      this.projectHubService.didAnnotationTypesChangedFor(this.annotationsStore.projectId)
    ) {
      if (this.projectHubService.didQuestionsChangedFor(this.annotationsStore.projectId)) {
        const result = this.projectHubService.popNewQuestionsFor(this.annotationsStore.projectId);
        this.freeAccessStore.questions.replace([...result]);
      }
    }
    this.assignQuestions();
  }

  async rejectAnnotationAsync(reason: string = ''): Promise<void> {
    if (this.freeAccessStore.freeAccessMode === FreeAccessMode.REVIEWCORRECT) {
      await this.editAndAcceptOrRejectAnnotationAsync(false, reason);
    } else {
      this.undoRedoService.clearDrawingHistory();
      this.undoRedoService.clearHistory();

      if (!this.annotationsStore.id) {
        throw 'Annotation Id can not be empty';
      }

      const request: IRejectAnnotationRequest = { reason, id: this.annotationsStore.id, duration: this.timer.duration };

      const result = await this.freeAccessAnnotationApiService.rejectAsync(request);

      await this.handleResult(result);
      if (result === undefined) {
        if (reason) {
          this.reviewRejectionReasonBl.handleRejection(reason);
        }
        this.freeAccessStore.rejectionReason = reason;
      }
    }

    await this.changeModeAsync(FreeAccessMode.VIEW, AnnotateStage.REJECTED);
  }

  async cancelCorrectAnnotationAsync(): Promise<void> {
    await this.changeModeAsync(FreeAccessMode.REVIEW);
  }

  async cancelReviewAnnotationAsync(): Promise<void> {
    await this.annotationApiService.unlockImageAsync(this.freeAccessStore.imageId, this.freeAccessStore.projectId, false);
    await this.changeModeAsync(FreeAccessMode.VIEW);
  }

  async cancelCreateAnnotationAsync(): Promise<void> {
    await this.annotationApiService.unlockImageAsync(this.freeAccessStore.imageId, this.freeAccessStore.projectId, false);
    await this.changeModeAsync(FreeAccessMode.VIEW);
  }

  async discardAnnotationAsync(editorMode: EditorMode): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annotation Id can not be empty';
    }

    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();

    let result = undefined;
    const request = { id: this.annotationsStore.id, duration: this.timer.duration };

    switch (editorMode) {
      case EditorMode.FREEACCESS_ANNOTATION:
        result = await this.freeAccessAnnotationApiService.discardDuringAnnotationAsync(request);
        break;
      case EditorMode.FREEACCESS_EDIT:
        result = await this.freeAccessAnnotationApiService.discardDuringFreeAccessEditAsync(request);
        break;
      case EditorMode.FREEACCESS_REVIEW:
        result = await this.freeAccessAnnotationApiService.discardDuringReviewAsync(request);
        break;
      case EditorMode.FREEACCESS_REVIEWEDIT:
        result = await this.freeAccessAnnotationApiService.discardDuringReviewEditAsync(request);
        break;
      case EditorMode.BATCH_ANNOTATION:
      case EditorMode.BATCH_REVIEW:
      case EditorMode.BATCH_REVIEWEDIT:
        break;
    }
    await this.handleResult(result);
    await this.changeModeAsync(FreeAccessMode.VIEW, AnnotateStage.NOTANNOTATED);
  }

  async acceptAnnotationAsync(): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annotation Id can not be empty';
    }

    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();

    const request: IAcceptAnnotationRequest = { id: this.annotationsStore.id, duration: this.timer.duration };

    const result = await this.freeAccessAnnotationApiService.acceptAsync(request);

    await this.handleResult(result);
    await this.changeModeAsync(FreeAccessMode.VIEW, AnnotateStage.REVIEWED);
  }

  async handleResult(result: void | ACDLError): Promise<void> {
    if (result instanceof ACDLError) {
      if (result.isBadRequestWithCode(['ANNOTATION_NOT_FOUND'])) {
        this.annotationsStore.image = undefined;
        this.imagesQueueService.areAnyImagesToLoad = false;
        await this.notificationsService.push(new ToastNotification(NotificationLevel.WARNING, 'operation_was_unsuccessful_dataset_changed'));
      } else if (result.isBadRequestWithCode(['IMAGE_ALREADY_LOCKED'])) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'image_is_locked_by_another_user'));
      } else {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'something_went_wrong'));
      }
    }
  }

  showImage(): void {
    const image = this.imagesQueueService.takeImageFromQueue();
    if (image === undefined) {
      return;
    }

    if (this.annotationsStore.image && this.annotationsStore.image.id === image.id) {
      this.loadAnnotations(image);
      this.undoRedoService.clearHistory();
      return;
    }
    this.uiStore.changeIsImageLoading(true);
    this.annotationsStore.setImageName(image.name, image.dataSetName, image.datasetStatus);
    setTimeout(
      action(() => {
        this.annotationsStore.image = image;
        this.loadAnnotations(image);
      }),
      0
    );
  }

  async editAndAcceptOrRejectAnnotationAsync(accept: boolean, rejectionReason?: string): Promise<void> {
    if (!(await this.freeDrawSegmentationService.clearAsync())) {
      return;
    }

    if (!this.annotationsStore.id || !this.annotationService.validateAnnotations()) {
      await this.annotationTypeBl.handleSubmitFixInFreeAccess();
      return;
    }

    const annotations = {
      id: this.annotationsStore.id,
      questions: this.annotationsStore.questions,
      segmentations: this.annotationsStore.segmentations.map((s) => ({
        id: s.id,
        questions: s.questions,
        feature: { type: s.feature.type, properties: s.feature.properties, geometry: s.feature.geometry },
        priority: s.priority,
      })),
    };

    let result: void | ACDLError | undefined = undefined;

    if (accept) {
      result = await this.freeAccessAnnotationApiService.editAndAcceptAsync(
        new EditAndAcceptAnnotationRequest(annotations, this.timer.duration, this.annotationsStore.id)
      );
    } else {
      result = await this.freeAccessAnnotationApiService.editAndRejectAsync(
        new EditAndRejectAnnotationRequest(rejectionReason, annotations, this.timer.duration, this.annotationsStore.id)
      );
    }

    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();

    if (result instanceof ACDLError) {
      if (result.isBadRequestWithCode(['ANNOTATION_NOT_FOUND'])) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.WARNING, 'operation_was_unsuccessful_dataset_changed'));
      } else if (result.isBadRequestWithCode(['IMAGE_ALREADY_LOCKED'])) {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'image_is_locked_by_another_user'));
      } else {
        await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'something_went_wrong'));
      }
      return;
    }
    await this.changeModeAsync(FreeAccessMode.VIEW, AnnotateStage.REVIEWED);
  }

  async rejectFixAnnotationAsync(): Promise<void> {
    await this.rejectAnnotationAsync();
  }

  async clearPreviousImageLockAsync(): Promise<void> {
    if (this.imageId && this.projectId && this.mode !== FreeAccessMode.VIEW) {
      await this.annotationApiService.unlockImageAsync(this.imageId, this.projectId, false);
    }
  }

  async unlockImagesAsync(): Promise<void> {
    if (this.projectId) {
      await this.annotationApiService.unlockUserImagesForProjectAsync(this.projectId);
    }
  }

  async submitAnnotationsForReviewAsync(): Promise<void> {
    if (!(await this.freeDrawSegmentationService.clearAsync())) {
      return;
    }
    if (!this.annotationService.validateAnnotations()) {
      await this.annotationTypeBl.handleInvalidSubmitAnnotationInFreeAccess();
      return;
    }

    const id = this.annotationsStore.id || v4();
    const resultTask = await this.freeAccessAnnotationApiService.submitForReviewAsync({
      id,
      projectId: this.projectId,
      imageId: this.imageId,
      duration: this.timer.duration,
      annotation: this.mapAnnotationDto(id),
    });

    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();

    if (resultTask instanceof ACDLError) {
      await this.handleErrors(resultTask);
      return;
    }

    await this.refreshQuestionsIfNeededAsync();
    this.annotationTypeBl.handleSubmitAnnotationInFreeAccess();
    await this.changeModeAsync(FreeAccessMode.VIEW, AnnotateStage.ANNOTATED);
  }

  async saveAnnotationDraftAsync(): Promise<void> {
    this.freeDrawSegmentationService.clear();

    const id = this.annotationsStore.id ?? v4();

    const result = await this.freeAccessAnnotationApiService.saveDraftAsync({
      id,
      projectId: this.projectId,
      imageId: this.imageId,
      duration: this.timer.duration,
      annotation: this.mapAnnotationDto(id),
    });

    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();

    if (result instanceof ACDLError) {
      await this.handleErrors(result);
      return;
    }

    await this.changeModeAsync(FreeAccessMode.VIEW, AnnotateStage.DRAFT);
  }

  async handleErrors(e: ACDLError): Promise<void> {
    if (e.isBadRequestWithCode(['PROJECT_NOT_FOUND'])) {
      this.annotationsStore.image = undefined;
      this.imagesQueueService.areAnyImagesToLoad = false;
    } else if (e.isBadRequestWithCode(['IMAGE_ALREADY_LOCKED'])) {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'image_is_locked_by_another_user'));
    } else {
      await this.notificationsService.push(new ToastNotification(NotificationLevel.ERROR, '', 'something_went_wrong'));
    }

    return;
  }

  async correctAnnotationAsync(): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annotation Id can not be empty';
    }

    if (this.freeAccessStore.status !== AnnotationStatus.DRAFT) {
      this.freeDrawSegmentationService.clear();
      if (!this.annotationService.validateAnnotations()) {
        return;
      }
    }

    const result = await this.freeAccessAnnotationApiService.correctAsync({
      id: this.annotationsStore.id,
      annotation: this.mapAnnotationDto(),
      duration: this.timer.duration,
    });

    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();

    if (result instanceof ACDLError) {
      return;
    }

    await this.changeModeAsync(FreeAccessMode.VIEW);
  }

  async editAnnotationAsync(): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annotation Id can not be empty';
    }

    if (this.freeAccessStore.status !== AnnotationStatus.DRAFT) {
      this.freeDrawSegmentationService.clear();
      if (!this.annotationService.validateAnnotations()) {
        return;
      }
    }

    const result = await this.freeAccessAnnotationApiService.editAsync({
      id: this.annotationsStore.id,
      annotation: this.mapAnnotationDto(),
      duration: this.timer.duration,
    });

    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();

    if (result instanceof ACDLError) {
      return;
    }

    await this.changeModeAsync(FreeAccessMode.VIEW);
  }

  async correctAndSubmitAnnotationForReviewAsync(): Promise<void> {
    if (!this.annotationsStore.id) {
      throw 'Annotation Id can not be empty';
    }

    this.freeDrawSegmentationService.clear();
    if (!this.annotationService.validateAnnotations()) {
      return;
    }

    const result = await this.freeAccessAnnotationApiService.correctAndSubmitForReviewAsync({
      id: this.annotationsStore.id,
      duration: this.timer.duration,
      annotation: this.mapAnnotationDto(),
    });

    this.undoRedoService.clearDrawingHistory();
    this.undoRedoService.clearHistory();

    if (result instanceof ACDLError) {
      return;
    }

    await this.changeModeAsync(FreeAccessMode.VIEW, AnnotateStage.ANNOTATED);
  }

  async updateLockImageInfoAsync(): Promise<void> {
    const result = await this.freeAccessApiService.getImageLockInfo(this.freeAccessStore.projectId, this.freeAccessStore.imageId);
    if (result instanceof ACDLError) {
      return;
    }

    this.freeAccessStore.lockedByName = result;
  }

  private mapAnnotationDto(id?: string): IAnnotationDto {
    const annotationId = id || this.annotationsStore.id;

    if (!annotationId) {
      throw 'Annotation Id can not be empty';
    }

    return {
      id: annotationId,
      questions: this.annotationsStore.questions,
      segmentations: this.annotationsStore.segmentations.map((s) => ({
        id: s.id,
        questions: s.questions,
        feature: { type: s.feature.type, properties: s.feature.properties, geometry: s.feature.geometry },
        priority: 0,
      })),
    };
  }

  async getProjectAnnotationInfoAsync(projectId: string): Promise<void> {
    const result = await this.annotationApiService.getProjectAnnotationInfoAsync(projectId);
    this.freeAccessStore.projectStatus = result.projectStatus;
  }
}
